/**
 * Custom hook for managing game pause/resume functionality.
 * Connects pause state with Phaser game scene.
 */

import { useEffect } from 'react';
import { useGameStateStore } from '@shared/stores';

interface UseGamePauseOptions {
  scene: unknown | null;
}

/**
 * Hook for managing game pause/resume with Phaser scene.
 */
export function useGamePause({ scene }: UseGamePauseOptions) {
  const { isPaused } = useGameStateStore();

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    // Type guard for TilemapScene - has pauseGame() and resumeGame() methods
    const tilemapScene = scene as {
      pauseGame?: () => void;
      resumeGame?: () => void;
    };

    // Pause or resume the Phaser scene
    if (isPaused) {
      // Pause the scene
      if (tilemapScene.pauseGame) {
        tilemapScene.pauseGame();
      }
    } else {
      // Resume the scene
      if (tilemapScene.resumeGame) {
        tilemapScene.resumeGame();
      }
    }
  }, [scene, isPaused]);
}
